import { useEffect, useState } from "react";
import {
  Modal,
  Form,
  DatePicker,
  Radio,
  Space,
  Alert,
  Spin,
  Row,
  Col,
  Statistic,
  List,
  Typography,
} from "antd";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const SchoolPaySyncModal = ({ visible, onCancel, onSync, loading, result }) => {
  const [form] = Form.useForm();
  const [syncMode, setSyncMode] = useState("today");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!loading) return undefined;

    setElapsedSeconds(0);
    const intervalId = setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [loading]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      let syncData = {};

      if (syncMode === "single" && values.date) {
        syncData.date = values.date.format("YYYY-MM-DD");
      } else if (syncMode === "range" && values.dateRange) {
        syncData.from_date = values.dateRange[0].format("YYYY-MM-DD");
        syncData.to_date = values.dateRange[1].format("YYYY-MM-DD");
      }
      // For 'today' mode, we send empty object (backend defaults to today)

      await onSync(syncData);
    } catch (error) {
      console.error("Sync validation error:", error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSyncMode("today");
    onCancel();
  };

  return (
    <Modal
      title="Sync SchoolPay Transactions"
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText={result ? "Sync Again" : "Sync Transactions"}
      cancelText={result ? "Close" : "Cancel"}
      confirmLoading={loading}
      width={600}
      okButtonProps={{ disabled: loading }}
      cancelButtonProps={{ disabled: loading }}
      closable={!loading}
      maskClosable={!loading}
      keyboard={!loading}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {loading && (
          <Alert
            type="info"
            showIcon
            message="Processing SchoolPay transactions"
            description={
              <Space>
                <Spin size="small" />
                <span>
                  Waiting for all transaction jobs to finish. Elapsed time:{" "}
                  {elapsedSeconds}s
                </span>
              </Space>
            }
          />
        )}

        {result?.pending && (
          <Alert
            type="warning"
            showIcon
            message="Sync is still running"
            description={
              result.message ||
              "Transactions are still processing in the background."
            }
          />
        )}

        {result && !result.pending && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Alert
              type={result.errors > 0 ? "warning" : "success"}
              showIcon
              message={result.message || "SchoolPay sync completed"}
            />
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={8}>
                <Statistic
                  title="Total"
                  value={result.totalTransactions || 0}
                />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic
                  title="Processed"
                  value={result.processed || 0}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic title="Duplicates" value={result.duplicates || 0} />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic title="Skipped" value={result.skipped || 0} />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic
                  title="Errors"
                  value={result.errors || 0}
                  valueStyle={{
                    color: result.errors > 0 ? "#ff4d4f" : undefined,
                  }}
                />
              </Col>
            </Row>
            {Array.isArray(result.errorDetails) &&
              result.errorDetails.length > 0 && (
                <List
                  size="small"
                  header={<Text strong>Error details</Text>}
                  bordered
                  dataSource={result.errorDetails}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                />
              )}
          </Space>
        )}

        <Form form={form} layout="vertical">
          <Form.Item label="Sync Mode" name="syncMode">
            <Radio.Group
              value={syncMode}
              onChange={(e) => setSyncMode(e.target.value)}
              buttonStyle="solid"
              disabled={loading}
            >
              <Space direction="vertical">
                <Radio value="today">Today's Transactions (Default)</Radio>
                <Radio value="single">Specific Date</Radio>
                <Radio value="range">Date Range</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {syncMode === "single" && (
            <Form.Item
              name="date"
              label="Select Date"
              rules={[{ required: true, message: "Please select a date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                disabled={loading}
              />
            </Form.Item>
          )}

          {syncMode === "range" && (
            <Form.Item
              name="dateRange"
              label="Select Date Range"
              rules={[
                { required: true, message: "Please select a date range" },
              ]}
            >
              <RangePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                disabled={loading}
              />
            </Form.Item>
          )}
        </Form>

        <Alert
          message="Note"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Duplicate transactions will be automatically skipped</li>
              <li>Invalid or problematic transactions will go to Suspense</li>
              <li>All valid transactions will be auto-allocated immediately</li>
            </ul>
          }
          type="warning"
        />
      </Space>
    </Modal>
  );
};

export default SchoolPaySyncModal;
